import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Cookies from "js-cookie";
import {
    Box,
    Typography,
    Paper,
    TextField,
    IconButton,
    InputAdornment,
    Badge,
    CircularProgress,
    Popper,
    ClickAwayListener,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Divider,
    useTheme,
    useMediaQuery,
    Switch,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { List as VirtualizedList, AutoSizer } from "react-virtualized";
import "react-virtualized/styles.css";
import {
    Send,
    EmojiEmotions,
    Person,
    SmartToy,
    KeyboardArrowDown,
    Close,
    Search,
    Chat,
    AccessTime,
    Done,
    DoneAll,
    Description,
    AttachFile,
    Image,
    VideoFile,
    InsertDriveFile,
    Cancel,
    Download,
    PersonAddOutlined,
    EditOutlined,
    CheckOutlined,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import TemplateMessageBubble from "./testMessage/TemplateMessageBubble";

// Format phone number for display (add spaces for readability)
const formatPhoneForDisplay = (phoneNumber) => {
    if (!phoneNumber) return "";
    const digits = phoneNumber.replace(/\D/g, "");
    if (digits.length >= 10) {
        const countryCode = digits.length > 10 ? `+${digits.slice(0, -10)} ` : "";
        const number = digits.slice(-10);
        return `${countryCode}${number.slice(0, 5)} ${number.slice(5)}`;
    }
    return phoneNumber;
};

// Professional emojis for business chat
const professionalEmojis = [
    "🙂", "😊", "😄", "😃", "😁", "🙏", "👍", "👎", "👌", "✌️",
    "🤝", "👏", "🙌", "💪", "✅", "❌", "✔️", "⭐", "🌟", "💯",
];

// Message Status Icon Component
const MessageStatusIcon = ({ status }) => {
    if (!status) return null;

    const statusUpper = status.toUpperCase();
    
    switch (statusUpper) {
        case "PENDING":
            return (
                <AccessTime
                    sx={{
                        fontSize: 14,
                        opacity: 0.7,
                        ml: 0.5,
                    }}
                />
            );
        case "SENT":
            return (
                <Done
                    sx={{
                        fontSize: 14,
                        opacity: 0.7,
                        ml: 0.5,
                    }}
                />
            );
        case "DELIVERED":
            return (
                <DoneAll
                    sx={{
                        fontSize: 14,
                        opacity: 0.7,
                        ml: 0.5,
                        color: "rgba(0, 0, 0, 0.6)",
                    }}
                />
            );
        case "READ":
            return (
                <DoneAll
                    sx={{
                        fontSize: 14,
                        opacity: 1,
                        ml: 0.5,
                        color: "#4fc3f7", // Blue color like WhatsApp
                    }}
                />
            );
        case "FAILED":
            return (
                <Box
                    sx={{
                        fontSize: 14,
                        opacity: 0.7,
                        ml: 0.5,
                        color: "error.main",
                        display: "inline-flex",
                        alignItems: "center",
                    }}
                >
                    ⚠
                </Box>
            );
        default:
            return null;
    }
};

const FullScreenChatWindow = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    
    const [selectedContact, setSelectedContact] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [messageInput, setMessageInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [messages, setMessages] = useState({});
    const [recipients, setRecipients] = useState([]);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 50,
        total: 0,
    });
    const [conversationPagination, setConversationPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 50,
        total: 0,
    });
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [unreadCount, setUnreadCount] = useState(0);
    const [lastMessageId, setLastMessageId] = useState(null);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const contactsListRef = useRef(null);

    // ── Bot & CRM state (per selected contact) ────────────────────────────
    const [botEnabled, setBotEnabled]       = useState(null);
    const [botToggling, setBotToggling]     = useState(false);
    const [crmExists, setCrmExists]         = useState(false);
    const [crmName, setCrmName]             = useState("");
    const [crmContact, setCrmContact]       = useState(null);
    const [addCrmOpen, setAddCrmOpen]       = useState(false);
    const [editNameOpen, setEditNameOpen]   = useState(false);
    const [crmNameInput, setCrmNameInput]   = useState("");
    const [savingCrm, setSavingCrm]         = useState(false);
    const SOURCE_OPTIONS = ["Facebook", "Google", "Manual", "WhatsApp", "Referral"];
    const EMPTY_CRM_FORM = { name: "", email: "", company_name: "", source: "", status: "interested", is_bot_enabled: true };
    const [crmForm, setCrmForm]             = useState(EMPTY_CRM_FORM);
    const messagesListRef = useRef(null);
    const notify = useNotification();
    const roles = useMemo(
        () => (Cookies.get("userRole") || "").split(",").map((r) => r.trim()),
        []
    );
    const isWbAgent = roles.includes("WB-AGENT");
    const hasCrmRole = roles.includes("CRM-ADMIN") || roles.includes("CRM-USER");
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojiButtonRef = useRef(null);
    const [templateDetailsCache, setTemplateDetailsCache] = useState({});
    const [loadingTemplates, setLoadingTemplates] = useState({});
    
    // Media send state
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaPreviewUrl, setMediaPreviewUrl] = useState("");
    const [mediaType, setMediaType] = useState(null);
    const [uploadingMedia, setUploadingMedia] = useState(false);
    const [sendingMedia, setSendingMedia] = useState(false);
    const fileInputRef = useRef(null);
    
    // Refs to track loading states without causing re-renders
    const loadingMessagesRef = useRef(false);
    const loadingMoreMessagesRef = useRef(false);
    const messagesRef = useRef({});
    const selectedContactRef = useRef(null);

    // Format phone number: remove spaces, +, and keep only digits
    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return "";
        return phoneNumber.replace(/\D/g, "");
    };

    // Mark messages as read for a recipient
    const markAsRead = useCallback(async (recipientPhone) => {
        if (!recipientPhone) return;

        const formattedPhone = formatPhoneNumber(recipientPhone);
        if (!formattedPhone) return;

        try {
            const response = await axiosInstance.post(
                `${base_url}/whatsapp-business/chat/messages/mark-as-read`,
                {
                    recipient: formattedPhone,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                }
            );

            if (response.data && response.data.success) {
                const messagesMarkedAsRead = response.data.data?.messages_marked_as_read || 0;
                
                // Update recipient unread count to 0 by matching formatted phone numbers
                setRecipients((prev) =>
                    prev.map((recipient) => {
                        const recipientFormatted = formatPhoneNumber(recipient.recipient);
                        return recipientFormatted === formattedPhone
                            ? { ...recipient, unreadCount: 0 }
                            : recipient;
                    })
                );

                return messagesMarkedAsRead;
            }
        } catch (error) {
            console.error("Error marking messages as read:", error);
            // Don't show error notification as this is a background operation
        }
        
        return 0;
    }, []);

    // Fetch template details
    const fetchTemplateDetails = useCallback(async (templateId) => {
        if (!templateId) return null;
        
        if (templateDetailsCache[templateId]) {
            return templateDetailsCache[templateId];
        }

        if (loadingTemplates[templateId]) {
            return null;
        }

        setLoadingTemplates((prev) => ({ ...prev, [templateId]: true }));

        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/template/${templateId}/details`,
                {
                    params: {
                        fields: "id,name,language,category,status,components,previous_category,parameter_format"
                    },
                    headers: {
                        Accept: "application/json",
                    },
                }
            );

            if (response.data && response.data.id) {
                const templateData = response.data;
                setTemplateDetailsCache((prev) => ({
                    ...prev,
                    [templateId]: templateData,
                }));
                return templateData;
            }
        } catch (error) {
            console.error(`Error fetching template details for ${templateId}:`, error);
        } finally {
            setLoadingTemplates((prev) => {
                const newState = { ...prev };
                delete newState[templateId];
                return newState;
            });
        }

        return null;
    }, [templateDetailsCache, loadingTemplates]);

    // Update refs when loading states change
    useEffect(() => {
        loadingMessagesRef.current = loadingMessages;
    }, [loadingMessages]);

    useEffect(() => {
        loadingMoreMessagesRef.current = loadingMoreMessages;
    }, [loadingMoreMessages]);

    // Keep messagesRef in sync with messages state
    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    // Keep selectedContactRef in sync with selectedContact state
    useEffect(() => {
        selectedContactRef.current = selectedContact;
    }, [selectedContact]);

    // Fetch conversation messages for a recipient
    const fetchConversationMessages = useCallback(async (recipientPhone, page = 1, append = false, isAutoRefetch = false) => {
        if (loadingMoreMessagesRef.current && append) return;

        const formattedPhone = formatPhoneNumber(recipientPhone);
        const currentMessages = messagesRef.current[formattedPhone] || [];
        const previousLastMessageId = currentMessages.length > 0 
            ? currentMessages[currentMessages.length - 1].id 
            : null;
        
        let savedScrollTop = null;
        let userIsAtBottom = true;
        if (isAutoRefetch && messagesListRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = messagesListRef.current;
            savedScrollTop = scrollTop;
            userIsAtBottom = scrollHeight - scrollTop - clientHeight < 50;
        }

        if (append) {
            setLoadingMoreMessages(true);
            loadingMoreMessagesRef.current = true;
        } else {
            setLoadingMessages(true);
            loadingMessagesRef.current = true;
        }

        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/chat/messages/conversation`,
                {
                    params: {
                        recipient: formattedPhone,
                        page: page,
                        per_page: 50,
                    },
                    headers: {
                        Accept: "application/json",
                    },
                }
            );

            if (response.data && response.data.success) {
                const { incoming = [], outgoing = [] } = response.data.data || {};

                // Helper function to process a message
                const processMessage = (msg, senderType) => {
                    const hasTemplateId = !!msg.template_id;
                    
                    let parsedPayload = null;
                    if (msg.payload) {
                        try {
                            parsedPayload = typeof msg.payload === 'string' 
                                ? JSON.parse(msg.payload) 
                                : msg.payload;
                        } catch (e) {
                            console.error("Error parsing payload:", e);
                            parsedPayload = null;
                        }
                    }

                    const messageObj = {
                        id: msg.id,
                        text: msg.message_content || "",
                        sender: senderType,
                        timestamp: new Date(msg.created_at).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                        }),
                        created_at: msg.created_at,
                        message_id: msg.message_id,
                        status: msg.status,
                        media_link: msg.media_link || null,
                        media_type: msg.media_type || null,
                        is_bot_reply: senderType === "bot" ? (msg.is_bot_reply === true) : false,
                        sender_name: senderType === "bot" ? (msg.sender_name || null) : null,
                    };

                    if (hasTemplateId) {
                        messageObj.is_template = true;
                        messageObj.template_id = msg.template_id;
                        messageObj.payload = parsedPayload;
                    } else {
                        messageObj.is_template = false;
                        messageObj.template_id = null;
                        messageObj.payload = null;
                    }

                    return messageObj;
                };

                const allMessages = [
                    ...incoming.map((msg) => processMessage(msg, "user")),
                    ...outgoing.map((msg) => processMessage(msg, "bot")),
                ];

                // Fetch template details for all messages with template_id
                const templateIds = new Set();
                allMessages.forEach((msg) => {
                    if (msg.template_id) {
                        templateIds.add(msg.template_id);
                    }
                });

                const templatePromises = Array.from(templateIds).map((templateId) =>
                    fetchTemplateDetails(templateId)
                );
                await Promise.all(templatePromises);

                // Sort by created_at (oldest first)
                allMessages.sort((a, b) => {
                    const dateA = new Date(a.created_at);
                    const dateB = new Date(b.created_at);
                    return dateA - dateB;
                });

                if (append) {
                    setMessages((prev) => {
                        const existingMessages = prev[formattedPhone] || [];
                        const combined = [...allMessages, ...existingMessages];
                        combined.sort((a, b) => {
                            const dateA = new Date(a.created_at);
                            const dateB = new Date(b.created_at);
                            return dateA - dateB;
                        });
                        const newState = {
                            ...prev,
                            [formattedPhone]: combined,
                        };
                        messagesRef.current = newState;
                        return newState;
                    });
                } else {
                    if (isAutoRefetch && previousLastMessageId) {
                        // During auto-refetch, we need to:
                        // 1. Update status of ALL existing messages (not just new ones)
                        // 2. Add only truly new messages
                        const existingMessages = messagesRef.current[formattedPhone] || [];
                        const existingIds = new Set(existingMessages.map(m => m.id));
                        
                        // Create a set of matched IDs to avoid duplicates
                        const matchedIds = new Set();
                        
                        // Update status of ALL existing messages by matching message_id or text + timestamp
                        const updatedMessages = existingMessages.map((existingMsg) => {
                            // Try to find matching message by message_id first
                            const matchById = allMessages.find(
                                (msg) => msg.message_id === existingMsg.message_id && msg.message_id
                            );
                            if (matchById) {
                                matchedIds.add(matchById.id);
                                // Update status even if ID is the same (status might have changed)
                                return { ...existingMsg, status: matchById.status, id: matchById.id };
                            }
                            
                            // Try to match by text content and timestamp (within 5 seconds)
                            const matchByContent = allMessages.find((msg) => {
                                if (msg.text === existingMsg.text && msg.sender === existingMsg.sender) {
                                    const existingTime = new Date(existingMsg.created_at).getTime();
                                    const msgTime = new Date(msg.created_at).getTime();
                                    const timeDiff = Math.abs(existingTime - msgTime);
                                    return timeDiff < 5000; // 5 seconds tolerance
                                }
                                return false;
                            });
                            if (matchByContent) {
                                matchedIds.add(matchByContent.id);
                                // Update status even if ID is the same (status might have changed)
                                return { ...existingMsg, status: matchByContent.status, id: matchByContent.id };
                            }
                            
                            return existingMsg;
                        });
                        
                        // Find truly new messages (not in existing messages and not matched)
                        const newMessages = allMessages.filter(m => 
                            !existingIds.has(m.id) && !matchedIds.has(m.id)
                        );
                        
                        // Update messages state
                        setMessages((prev) => {
                            const combined = [...updatedMessages, ...newMessages];
                            combined.sort((a, b) => {
                                const dateA = new Date(a.created_at);
                                const dateB = new Date(b.created_at);
                                return dateA - dateB;
                            });
                            
                            const newState = {
                                ...prev,
                                [formattedPhone]: combined,
                            };
                            messagesRef.current = newState;
                            return newState;
                        });
                        
                        // Handle new messages UI updates
                        if (newMessages.length > 0) {
                            if (userIsAtBottom) {
                                // User is at bottom - scroll to show new messages and mark as read
                                setTimeout(() => {
                                    if (messagesListRef.current) {
                                        messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
                                    }
                                    setIsAtBottom(true);
                                    setShowScrollButton(false);
                                    setUnreadCount(0);
                                    markAsRead(recipientPhone);
                                }, 100);
                            } else {
                                // User is not at bottom - show unread count
                                setUnreadCount((prev) => prev + newMessages.length);
                            }
                        }
                        
                        // Update last message ID
                        if (allMessages.length > 0) {
                            const currentLastMessageId = allMessages[allMessages.length - 1].id;
                            setLastMessageId(currentLastMessageId);
                        }
                    } else {
                        // Initial load or manual refresh - replace all messages, but preserve status updates
                        setMessages((prev) => {
                            const existingMessages = prev[formattedPhone] || [];
                            
                            // Track matched local message IDs to avoid duplicates
                            const matchedLocalIds = new Set();
                            
                            // Update status of existing messages by matching message_id or content
                            const updatedExisting = existingMessages.map((existingMsg) => {
                                // Try to find matching message by message_id first
                                const matchById = allMessages.find(
                                    (msg) => msg.message_id === existingMsg.message_id && msg.message_id
                                );
                                if (matchById) {
                                    matchedLocalIds.add(existingMsg.id);
                                    return { ...existingMsg, status: matchById.status, id: matchById.id };
                                }
                                
                                // Try to match by text content and timestamp (within 5 seconds)
                                const matchByContent = allMessages.find((msg) => {
                                    if (msg.text === existingMsg.text && msg.sender === existingMsg.sender) {
                                        const existingTime = new Date(existingMsg.created_at).getTime();
                                        const msgTime = new Date(msg.created_at).getTime();
                                        const timeDiff = Math.abs(existingTime - msgTime);
                                        return timeDiff < 5000; // 5 seconds tolerance
                                    }
                                    return false;
                                });
                                if (matchByContent) {
                                    matchedLocalIds.add(existingMsg.id);
                                    return { ...existingMsg, status: matchByContent.status, id: matchByContent.id };
                                }
                                
                                return existingMsg;
                            });
                            
                            // Merge: use API messages as source of truth, but keep any local messages not yet in API
                            const apiMessageIds = new Set(allMessages.map(m => m.id));
                            
                            // Keep only local messages that weren't matched (not in API yet)
                            const localOnlyMessages = updatedExisting.filter((msg) => {
                                if (apiMessageIds.has(msg.id)) return false;
                                if (matchedLocalIds.has(msg.id)) return false;
                                return true;
                            });
                            
                            const merged = [...allMessages, ...localOnlyMessages];
                            merged.sort((a, b) => {
                                const dateA = new Date(a.created_at);
                                const dateB = new Date(b.created_at);
                                return dateA - dateB;
                            });
                            
                            const newState = {
                                ...prev,
                                [formattedPhone]: merged,
                            };
                            messagesRef.current = newState;
                            return newState;
                        });

                        if (!isAutoRefetch || (isAutoRefetch && userIsAtBottom)) {
                            setTimeout(() => {
                                if (messagesListRef.current) {
                                    messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
                                }
                                setIsAtBottom(true);
                                setShowScrollButton(false);
                                if (isAutoRefetch) {
                                    setUnreadCount(0);
                                    // Mark messages as read when auto-refetch detects new messages and user is at bottom
                                    markAsRead(recipientPhone);
                                }
                            }, 100);
                        } else {
                            requestAnimationFrame(() => {
                                setTimeout(() => {
                                    if (messagesListRef.current && savedScrollTop !== null) {
                                        messagesListRef.current.scrollTop = savedScrollTop;
                                    }
                                }, 0);
                            });
                        }

                        if (allMessages.length > 0) {
                            const currentLastMessageId = allMessages[allMessages.length - 1].id;
                            if (!isAutoRefetch || userIsAtBottom) {
                                setLastMessageId(currentLastMessageId);
                            }
                        }
                    }
                }

                if (response.data.pagination) {
                    setConversationPagination(response.data.pagination);
                }
            }
        } catch (error) {
            console.error("Error fetching conversation messages:", error);
            notify(
                error.response?.data?.message || "Failed to fetch messages",
                "error"
            );
        } finally {
            setLoadingMessages(false);
            setLoadingMoreMessages(false);
            loadingMessagesRef.current = false;
            loadingMoreMessagesRef.current = false;
        }
    }, [markAsRead, fetchTemplateDetails, notify]);

    // Fetch recipients from API
    const fetchRecipients = async (page = 1, append = false) => {
        if (loadingMore && append) return;
        
        if (append) {
            setLoadingMore(true);
        } else {
            setLoadingRecipients(true);
        }

        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/chat/messages/incoming`,
                {
                    params: {
                        page: page,
                        per_page: 50,
                    },
                    headers: {
                        Accept: "application/json",
                    },
                }
            );

            if (response.data && response.data.success) {
                const newRecipients = response.data.data.map((item) => ({
                    id: item.id,
                    recipient: item.recipient,
                    phoneNumber: formatPhoneForDisplay(item.recipient),
                    contactName: item.contact_name || null,
                    message_id: item.message_id,
                    phone_number_id: item.phone_number_id,
                    updated_at: item.updated_at,
                    timestamp: new Date(item.updated_at).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                    }),
                    unreadCount: item.unread_count || 0,
                }));

                if (append) {
                    // Append mode: merge with existing recipients, deduplicate by ID
                    setRecipients((prev) => {
                        // Create a map of existing recipients by id to ensure uniqueness
                        const existingMap = new Map(prev.map(r => [r.id, r]));
                        
                        // Update or add new recipients (Map automatically handles duplicates by ID)
                        newRecipients.forEach(newRecipient => {
                            existingMap.set(newRecipient.id, newRecipient);
                        });
                        
                        // Convert back to array and sort by updated_at (most recent first)
                        return Array.from(existingMap.values()).sort((a, b) => {
                            return new Date(b.updated_at) - new Date(a.updated_at);
                        });
                    });
                } else {
                    // Non-append refresh: API is the single source of truth
                    // Deduplicate by ID using Map (in case API has duplicates, though it shouldn't)
                    const recipientsMap = new Map(newRecipients.map(r => [r.id, r]));
                    
                    // Convert to array and sort by updated_at (most recent first)
                    const finalArray = Array.from(recipientsMap.values()).sort((a, b) => {
                        return new Date(b.updated_at) - new Date(a.updated_at);
                    });
                    
                    // Always use API response directly - no merging with prev state
                    setRecipients(finalArray);
                }

                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            }
        } catch (error) {
            console.error("Error fetching recipients:", error);
            notify(
                error.response?.data?.message || "Failed to fetch recipients",
                "error"
            );
        } finally {
            setLoadingRecipients(false);
            setLoadingMore(false);
        }
    };

    // Load recipients on component mount
    useEffect(() => {
        fetchRecipients(1, false);
    }, []);

    // Auto-refetch recipients every 30 seconds to update unread counts
    useEffect(() => {
        const intervalId = setInterval(() => {
            if (!loadingRecipients && !loadingMore) {
                fetchRecipients(1, false);
            }
        }, 30000); // 30 seconds

        return () => {
            clearInterval(intervalId);
        };
    }, [loadingRecipients, loadingMore]);

    // Auto-refetch conversation messages every 15 seconds when a contact is selected
    useEffect(() => {
        if (!selectedContact) return;

        // selectedContact is now a formatted phone number
        const intervalId = setInterval(() => {
            // Check loading states using refs to avoid dependency issues
            if (!loadingMessagesRef.current && !loadingMoreMessagesRef.current) {
                fetchConversationMessages(selectedContact, 1, false, true);
            }
        }, 15000); // 15 seconds

        return () => {
            clearInterval(intervalId);
        };
    }, [selectedContact, fetchConversationMessages]);

    // Handle scroll for pagination (for virtualized list)
    const handleScroll = ({ scrollTop, scrollHeight, clientHeight }) => {
        if (!scrollHeight || !clientHeight) return;
        
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

        if (
            isNearBottom &&
            !loadingMore &&
            pagination.current_page < pagination.last_page
        ) {
            fetchRecipients(pagination.current_page + 1, true);
        }
    };

    // ── CRM helpers — re-run whenever the selected contact changes ───────────
    const fetchCrmContact = useCallback(async (phone) => {
        if (!phone) return;
        setBotEnabled(null);
        setCrmExists(false);
        setCrmName("");
        try {
            const res = await axiosInstance.get(`${base_url}/crm/contacts`, { params: { search: phone, per_page: 5 } });
            const paginator = res.data?.data ?? {};
            const list = Array.isArray(paginator.data) ? paginator.data : [];
            const contact = list.find((c) => String(c.phone) === String(phone));
            if (contact) {
                setBotEnabled(Boolean(contact.is_bot_enabled));
                setCrmExists(true);
                setCrmName(contact.name || "");
                setCrmContact(contact);
            } else {
                setBotEnabled(true);
                setCrmContact(null);
            }
        } catch { setBotEnabled(true); setCrmContact(null); }
    }, []);

    useEffect(() => {
        if (selectedContact) fetchCrmContact(selectedContact);
        else { setBotEnabled(null); setCrmExists(false); setCrmName(""); setCrmContact(null); }
    }, [selectedContact, fetchCrmContact]);

    const toggleBot = async () => {
        const newVal = !botEnabled;
        setBotToggling(true);
        try {
            await axiosInstance.post(`${base_url}/crm/contacts/bot-enabled`, { phone: selectedContact, is_bot_enabled: newVal });
            setBotEnabled(newVal);
            notify(`Auto bot reply ${newVal ? "enabled" : "disabled"}`, "success");
        } catch (err) {
            notify(err?.response?.data?.message || "Failed to update bot setting", "error");
        } finally { setBotToggling(false); }
    };

    const handleAddToCrm = async () => {
        setSavingCrm(true);
        try {
            await axiosInstance.post(`${base_url}/crm/contacts/move-to-crm`, {
                phone: selectedContact,
                ...(crmForm.name.trim()         ? { name: crmForm.name.trim() }               : {}),
                ...(crmForm.email.trim()        ? { email: crmForm.email.trim() }              : {}),
                ...(crmForm.company_name.trim() ? { company_name: crmForm.company_name.trim() }: {}),
                ...(crmForm.source.trim()       ? { source: crmForm.source.trim() }            : {}),
                status:         crmForm.status,
                is_bot_enabled: crmForm.is_bot_enabled,
            });
            notify(`Contact ${crmExists ? "updated" : "added to CRM"} successfully`, "success");
            setAddCrmOpen(false);
            setCrmForm(EMPTY_CRM_FORM);
            fetchCrmContact(selectedContact);
        } catch (err) {
            notify(err?.response?.data?.message || "Failed to save contact", "error");
        } finally { setSavingCrm(false); }
    };

    const handleEditName = async () => {
        if (!crmNameInput.trim()) return;
        setSavingCrm(true);
        try {
            await axiosInstance.post(`${base_url}/crm/contacts/edit-name`, { phone: selectedContact, name: crmNameInput.trim() });
            notify("Contact name updated successfully", "success");
            setEditNameOpen(false);
            setCrmName(crmNameInput.trim());
            setCrmNameInput("");
            // also refresh recipients list so sidebar name updates
            fetchRecipients(1, false);
        } catch (err) {
            notify(err?.response?.data?.message || "Failed to update name", "error");
        } finally { setSavingCrm(false); }
    };
    // ─────────────────────────────────────────────────────────────────────────

    const filteredContacts = recipients.filter((contact) =>
        contact.recipient.includes(searchQuery.replace(/\D/g, "")) ||
        contact.phoneNumber.includes(searchQuery) ||
        (contact.contactName && contact.contactName.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    // selectedContact is now a formatted phone number, use it directly as key
    const currentMessages = selectedContact ? messages[selectedContact] || [] : [];

    // Group messages by date and inject date headers like "5 March 2026"
    const messagesWithDateHeaders = React.useMemo(() => {
        if (!currentMessages || currentMessages.length === 0) return [];

        const result = [];
        let lastDateLabel = null;

        currentMessages.forEach((msg) => {
            const createdAt = msg.created_at || msg.timestamp_raw || null;
            let dateLabel = null;
            if (createdAt) {
                const d = new Date(createdAt);
                if (!isNaN(d.getTime())) {
                    const day = d.getDate();
                    const month = d.toLocaleString("en-US", { month: "long" });
                    const year = d.getFullYear();
                    dateLabel = `${day} ${month} ${year}`;
                }
            }

            if (dateLabel && dateLabel !== lastDateLabel) {
                result.push({
                    __type: "date-header",
                    id: `date-${dateLabel}-${msg.id}`,
                    label: dateLabel,
                });
                lastDateLabel = dateLabel;
            }

            result.push(msg);
        });

        return result;
    }, [currentMessages]);

    const handleContactSelect = async (contactRecipient) => {
        const formattedPhone = formatPhoneNumber(contactRecipient);
        setSelectedContact(formattedPhone);
        selectedContactRef.current = formattedPhone;
        setUnreadCount(0);
        setIsAtBottom(true);
        setShowScrollButton(false);
        setLastMessageId(null);
        
        // Mark messages as read when contact is selected
        await markAsRead(contactRecipient);
        
        fetchConversationMessages(formattedPhone, 1, false);
    };

    // Row renderer for virtualized contacts list
    const rowRenderer = useCallback(({ key, index, style }) => {
        const contact = filteredContacts[index];
        if (!contact) return null;

        return (
            <div key={contact.id} style={style}>
                <ListItem
                    button
                    onClick={() => handleContactSelect(contact.recipient)}
                    sx={{
                        backgroundColor:
                            selectedContact === formatPhoneNumber(contact.recipient)
                                ? WB_UI.color.softSuccessBg
                                : "transparent",
                        "&:hover": {
                            backgroundColor: WB_UI.color.softInfoBg,
                        },
                        cursor: "pointer",
                        height: "72px",
                    }}
                >
                    <ListItemAvatar>
                        <Badge
                            badgeContent={contact.unreadCount}
                            invisible={contact.unreadCount === 0}
                            sx={{
                                "& .MuiBadge-badge": {
                                    backgroundColor: WB_UI.color.whatsappDark,
                                },
                            }}
                        >
                            <Avatar
                                sx={{
                                    bgcolor: WB_UI.color.whatsappDark,
                                    width: 48,
                                    height: 48,
                                }}
                            >
                                <Person sx={{ fontSize: 28 }} />
                            </Avatar>
                        </Badge>
                    </ListItemAvatar>
                    <ListItemText
                        primary={
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                                >
                                    {contact.contactName || contact.phoneNumber}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {contact.timestamp}
                                </Typography>
                            </Box>
                        }
                        secondary={
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    fontSize: "0.75rem",
                                }}
                            >
                                {contact.contactName ? contact.phoneNumber : contact.recipient}
                            </Typography>
                        }
                    />
                </ListItem>
                {index < filteredContacts.length - 1 && <Divider />}
            </div>
        );
    }, [filteredContacts, selectedContact, handleContactSelect]);

    const handleSendMessage = async () => {
        if (!messageInput.trim()) {
            return;
        }

        // selectedContact is now a formatted phone number
        const selectedContactData = recipients.find((c) => formatPhoneNumber(c.recipient) === selectedContact);
        if (!selectedContactData) {
            notify("Contact not found", "error");
            return;
        }

        const phoneNumber = selectedContact; // Already formatted
        if (!phoneNumber) {
            notify("Invalid phone number", "error");
            return;
        }

        setLoading(true);
        const messageText = messageInput.trim();

        try {
            const response = await axiosInstance.post(
                `${base_url}/whatsapp-business/chat/messages/send`,
                {
                    to: phoneNumber,
                    message: messageText,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                }
            );

            if (response.data) {
                const newMessage = {
                    id: response.data.id || Date.now(), // Use API message ID if available
                    text: messageText,
                    sender: "bot",
                    timestamp: new Date().toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                    }),
                    created_at: response.data.created_at || new Date().toISOString(),
                    message_id: response.data.message_id || null,
                    is_template: false,
                    status: response.data.status || "PENDING", // Use status from API or default to PENDING
                };

                setMessages((prev) => {
                    const newState = {
                        ...prev,
                        [selectedContact]: [...(prev[selectedContact] || []), newMessage],
                    };
                    messagesRef.current = newState;
                    return newState;
                });
                notify("Message sent successfully", "success");
                setMessageInput("");
                
                // Trigger a refetch after a short delay to get updated status
                setTimeout(() => {
                    scrollToBottom();
                    if (!loadingMessagesRef.current && !loadingMoreMessagesRef.current) {
                        fetchConversationMessages(selectedContact, 1, false, true);
                    }
                }, 2000); // Wait 2 seconds for message to be processed
            } else {
                throw new Error("Failed to send message");
            }
        } catch (error) {
            console.error("Error sending message:", error);
            const errorMessage =
                error.response?.data?.message ||
                error.response?.data?.error ||
                "Failed to send message";
            notify(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey && !loading && !uploadingMedia && !sendingMedia) {
            e.preventDefault();
            if (mediaFile) {
                handleMediaUpload();
            } else {
                handleSendMessage();
            }
        }
    };

    const handleMessagesScroll = async (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        setIsAtBottom(isNearBottom);
        setShowScrollButton(!isNearBottom);
        
        if (isNearBottom && unreadCount > 0) {
            setUnreadCount(0);
            const currentMessages = messages[selectedContact] || [];
            if (currentMessages.length > 0) {
                setLastMessageId(currentMessages[currentMessages.length - 1].id);
            }
            
            // Mark messages as read when user scrolls to bottom and sees new messages
            // selectedContact is now a formatted phone number
            await markAsRead(selectedContact);
        }
        
        // Load more when scrolling to top
        if (
            scrollTop < 100 &&
            !loadingMoreMessages &&
            conversationPagination.current_page < conversationPagination.last_page &&
            selectedContact
        ) {
            // selectedContact is now a formatted phone number
            fetchConversationMessages(
                selectedContact,
                conversationPagination.current_page + 1,
                true
            );
        }
    };

    const scrollToBottom = async () => {
        if (messagesListRef.current) {
            messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
            setIsAtBottom(true);
            setShowScrollButton(false);
            
            if (unreadCount > 0) {
                setUnreadCount(0);
                // Mark messages as read when user clicks scroll to bottom button
                // selectedContact is now a formatted phone number
                await markAsRead(selectedContact);
            }
            
            const currentMessages = messages[selectedContact] || [];
            if (currentMessages.length > 0) {
                setLastMessageId(currentMessages[currentMessages.length - 1].id);
            }
        }
    };

    const handleClose = () => {
        // Navigate to the chatbot page
        navigate("/whatsapp-business/chatbot");
    };

    const handleEmojiClick = (emoji) => {
        setMessageInput((prev) => prev + emoji);
        setShowEmojiPicker(false);
    };

    const handleEmojiButtonClick = () => {
        setShowEmojiPicker((prev) => !prev);
    };

    // Handle media file selection
    const handleMediaFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Determine media type from file
        const fileType = file.type.toLowerCase();
        let detectedType = null;
        if (fileType.startsWith("image/")) {
            detectedType = "image";
        } else if (fileType.startsWith("video/")) {
            detectedType = "video";
        } else if (fileType.startsWith("audio/")) {
            detectedType = "audio";
        } else {
            detectedType = "document";
        }

        setMediaFile(file);
        setMediaType(detectedType);

        // Create preview URL
        if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setMediaPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setMediaPreviewUrl("");
        }

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Handle media upload and send
    const handleMediaUpload = async () => {
        if (!mediaFile || !selectedContact) {
            notify("Please select a file and a contact", "error");
            return;
        }

        setUploadingMedia(true);
        try {
            const formData = new FormData();
            formData.append("file", mediaFile);
            formData.append("type", mediaType);

            const response = await axiosInstance.post(
                `${base_url}/whatsapp-business/media/upload`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const mediaId = response.data?.id;
            const mediaTypeFromResponse = response.data?.type || null;
            const mediaLinkFromResponse = response.data?.cloudflare_path || null;

            if (mediaId && mediaLinkFromResponse) {
                // Upload successful, now send the media message
                await handleSendMediaMessage(mediaLinkFromResponse, mediaTypeFromResponse);
            } else {
                notify("Failed to get media URL from upload response", "error");
            }
        } catch (error) {
            console.error("Error uploading media:", error);
            notify(
                error.response?.data?.error ||
                    error.response?.data?.message ||
                    "Failed to upload media",
                "error"
            );
        } finally {
            setUploadingMedia(false);
        }
    };

    // Handle sending media message
    const handleSendMediaMessage = async (mediaLink, mediaTypeValue) => {
        if (!selectedContact || !mediaLink) {
            notify("Contact or media link not found", "error");
            return;
        }

        setSendingMedia(true);
        try {
            // Determine media_type for API (uppercase)
            let apiMediaType = "IMAGE";
            if (mediaTypeValue === "video" || mediaType === "video") {
                apiMediaType = "VIDEO";
            } else if (mediaTypeValue === "audio" || mediaType === "audio") {
                apiMediaType = "AUDIO";
            } else if (mediaTypeValue === "document" || mediaType === "document") {
                apiMediaType = "DOCUMENT";
            }

            const response = await axiosInstance.post(
                `${base_url}/whatsapp-business/media-message/send`,
                {
                    media_link: mediaLink,
                    media_type: apiMediaType.toLowerCase(), // API expects lowercase
                    recipient: selectedContact,
                    caption: messageInput.trim() || undefined, // Optional caption
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                }
            );

            if (response.data && response.data.success) {
                const newMessage = {
                    id: response.data.data?.message_id || Date.now(),
                    text: messageInput.trim() || "",
                    sender: "bot",
                    timestamp: new Date().toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                    }),
                    created_at: new Date().toISOString(),
                    message_id: response.data.data?.message_id || null,
                    is_template: false,
                    status: response.data.data?.status || "PENDING",
                    media_link: mediaLink,
                    media_type: apiMediaType,
                };

                setMessages((prev) => {
                    const newState = {
                        ...prev,
                        [selectedContact]: [...(prev[selectedContact] || []), newMessage],
                    };
                    messagesRef.current = newState;
                    return newState;
                });

                notify("Media message sent successfully", "success");
                
                // Clear media state
                setMediaFile(null);
                setMediaPreviewUrl("");
                setMediaType(null);
                setMessageInput("");

                // Trigger a refetch after a short delay to get updated status
                setTimeout(() => {
                    scrollToBottom();
                    if (!loadingMessagesRef.current && !loadingMoreMessagesRef.current) {
                        fetchConversationMessages(selectedContact, 1, false, true);
                    }
                }, 2000);
            } else {
                throw new Error("Failed to send media message");
            }
        } catch (error) {
            console.error("Error sending media message:", error);
            const errorMessage =
                error.response?.data?.message ||
                error.response?.data?.error ||
                "Failed to send media message";
            notify(errorMessage, "error");
        } finally {
            setSendingMedia(false);
        }
    };

    // Handle cancel media selection
    const handleCancelMedia = () => {
        setMediaFile(null);
        setMediaPreviewUrl("");
        setMediaType(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <>
        <Box
            sx={{
                height: "100vh",
                width: "100vw",
                display: "flex",
                overflow: "hidden",
                backgroundColor: "#f5f5f5",
            }}
        >
            {/* Left Panel - Contact List */}
            <Paper
                elevation={2}
                sx={{
                    width: isMobile ? "100%" : "350px",
                    display: "flex",
                    flexDirection: "column",
                    height: "100vh",
                    borderRight: 1,
                    borderColor: "divider",
                    ...WB_SX.card,
                    borderRadius: 0,
                    backgroundColor: WB_UI.color.cardBgStrong,
                }}
            >
                {/* Contact List Header with Close Button */}
                <Box
                    sx={{
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        backgroundColor: WB_UI.color.softInfoBg,
                        borderBottom: `1px solid ${WB_UI.color.border}`,
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#111827" }}>
                        WhatsApp Business Chat
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            backgroundColor: "error.main",
                            color: "white",
                            "&:hover": {
                                backgroundColor: "error.dark",
                            },
                        }}
                    >
                        <Close />
                    </IconButton>
                </Box>

                {/* Search Bar */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search contacts..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {/* Contacts List */}
                <Box
                    ref={contactsListRef}
                    sx={{ flex: 1, overflow: "hidden" }}
                >
                    {loadingRecipients && recipients.length === 0 ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                                p: 3,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : filteredContacts.length === 0 && !loadingRecipients ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                                p: 3,
                                color: "text.secondary",
                            }}
                        >
                            <Chat sx={{ fontSize: 48, mb: 2, opacity: 0.25, color: WB_UI.color.whatsappDark }} />
                            <Typography variant="body2">
                                No recipients found
                            </Typography>
                        </Box>
                    ) : (
                        <AutoSizer>
                            {({ height, width }) => (
                                <VirtualizedList
                                    width={width}
                                    height={height}
                                    rowCount={filteredContacts.length}
                                    rowHeight={72}
                                    rowRenderer={rowRenderer}
                                    rowKeyGetter={({ index }) => filteredContacts[index]?.id || index}
                                    onScroll={handleScroll}
                                    overscanRowCount={5}
                                />
                            )}
                        </AutoSizer>
                    )}
                    {loadingMore && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                p: 2,
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                backgroundColor: "background.paper",
                            }}
                        >
                            <CircularProgress size={24} />
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Right Panel - Chat Messages */}
            <Paper
                elevation={2}
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    height: "100vh",
                    position: "relative",
                }}
            >
                {selectedContact ? (
                    <>
                        {/* Chat Header */}
                        <Box
                            sx={{
                                p: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                backgroundColor: WB_UI.color.softInfoBg,
                                borderBottom: `1px solid ${WB_UI.color.border}`,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Avatar
                                    sx={{
                                        bgcolor: WB_UI.color.whatsappDark,
                                        width: 48,
                                        height: 48,
                                    }}
                                >
                                    <Person sx={{ fontSize: 28 }} />
                                </Avatar>
                                <Box>
                                    {(() => {
                                        const sel = recipients.find((c) => formatPhoneNumber(c.recipient) === selectedContact);
                                        return (
                                            <>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                                        {sel?.contactName || sel?.phoneNumber || "Unknown"}
                                                    </Typography>
                                                    {sel?.contactName && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: "#6b7280",
                                                                backgroundColor: "#f3f4f6",
                                                                border: "1px solid #e5e7eb",
                                                                borderRadius: 1,
                                                                px: 0.75,
                                                                py: 0.2,
                                                                fontWeight: 500,
                                                                fontSize: "0.7rem",
                                                            }}
                                                        >
                                                            {sel.phoneNumber}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {sel?.contactName ? "CRM contact · Showing recent messages" : "Showing recent messages for this number"}
                                                </Typography>
                                            </>
                                        );
                                    })()}
                                </Box>
                            </Box>

                            {/* ── Right‑side action buttons ─────────────────── */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

                                {/* Bot toggle — only visible to WB-AGENT */}
                                {isWbAgent && botEnabled !== null && (
                                    <Tooltip title={botEnabled ? "Auto bot reply ON – click to disable" : "Auto bot reply OFF – click to enable"} arrow>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                px: 1,
                                                py: 0.4,
                                                borderRadius: 2,
                                                border: `1px solid ${botEnabled ? "#ddd6fe" : "#e5e7eb"}`,
                                                backgroundColor: botEnabled ? "#f5f3ff" : "#f9fafb",
                                                cursor: botToggling ? "not-allowed" : "pointer",
                                                opacity: botToggling ? 0.7 : 1,
                                                transition: "all 0.2s",
                                            }}
                                            onClick={!botToggling ? toggleBot : undefined}
                                        >
                                            <SmartToy sx={{ fontSize: 15, color: botEnabled ? "#7c3aed" : "#9ca3af" }} />
                                            <Typography
                                                variant="caption"
                                                sx={{ fontWeight: 600, fontSize: "0.7rem", color: botEnabled ? "#7c3aed" : "#6b7280", lineHeight: 1 }}
                                            >
                                                Bot
                                            </Typography>
                                            <Switch
                                                size="small"
                                                checked={!!botEnabled}
                                                disabled={botToggling}
                                                onChange={toggleBot}
                                                onClick={(e) => e.stopPropagation()}
                                                sx={{
                                                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#7c3aed" },
                                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#7c3aed" },
                                                    ml: 0.25,
                                                }}
                                            />
                                        </Box>
                                    </Tooltip>
                                )}

                                {/* Add to CRM / In CRM — CRM-ADMIN or CRM-USER */}
                                {hasCrmRole && (
                                <Tooltip title={crmExists ? `In CRM${crmName ? ` · ${crmName}` : ""}` : "Add to CRM"} arrow>
                                    <Box>
                                        <Button
                                            size="small"
                                            variant={crmExists ? "outlined" : "contained"}
                                            startIcon={crmExists ? <CheckOutlined sx={{ fontSize: 14 }} /> : <PersonAddOutlined sx={{ fontSize: 14 }} />}
                                            onClick={() => {
                                                setCrmForm(crmContact ? {
                                                    name:           crmContact.name         || "",
                                                    email:          crmContact.email        || "",
                                                    company_name:   crmContact.company_name || "",
                                                    source:         crmContact.source       || "",
                                                    status:         crmContact.status       || "interested",
                                                    is_bot_enabled: Boolean(crmContact.is_bot_enabled),
                                                } : EMPTY_CRM_FORM);
                                                setAddCrmOpen(true);
                                            }}
                                            sx={{
                                                textTransform: "none",
                                                fontSize: "0.72rem",
                                                fontWeight: 600,
                                                borderRadius: 2,
                                                px: 1.25,
                                                py: 0.4,
                                                minWidth: 0,
                                                ...(crmExists
                                                    ? { borderColor: "#bbf7d0", color: "#166534", backgroundColor: "#f0fdf4", "&:hover": { backgroundColor: "#dcfce7", borderColor: "#86efac" } }
                                                    : { backgroundColor: "#1a2333", color: "#fff", "&:hover": { backgroundColor: "#374151" } }),
                                            }}
                                        >
                                            {crmExists ? "In CRM" : "Add to CRM"}
                                        </Button>
                                    </Box>
                                </Tooltip>
                                )}

                                {/* Edit Name — CRM role + in CRM */}
                                {hasCrmRole && crmExists && (
                                    <Tooltip title={`Edit name${crmName ? ` · ${crmName}` : ""}`} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => { setCrmNameInput(crmName); setEditNameOpen(true); }}
                                            sx={{
                                                border: "1px solid #e5e7eb",
                                                borderRadius: 1.5,
                                                p: 0.5,
                                                color: "#6b7280",
                                                "&:hover": { backgroundColor: "#f3f4f6", borderColor: "#d1d5db", color: "#111827" },
                                            }}
                                        >
                                            <EditOutlined sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>

                        {/* Messages Area */}
                        <Box
                            ref={messagesListRef}
                            sx={{
                                flex: 1,
                                overflowY: "auto",
                                p: 2,
                                backgroundColor: "#e5ddd5",
                                backgroundImage: `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAYAAADDPmHLAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAAdgAAAHYBTnsmCAAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAAAnSURBVHic7cEBDQAAAMIg+6c2BL4mAAAAAAAAAAAAAAAAAAD4B7a4AAFj8b8zAAAAAElFTkSuQmCC")`,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                            }}
                            onScroll={handleMessagesScroll}
                        >
                            {loadingMoreMessages && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        p: 1,
                                    }}
                                >
                                    <CircularProgress size={20} />
                                </Box>
                            )}
                            {loadingMessages && currentMessages.length === 0 ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: "100%",
                                        gap: 2,
                                    }}
                                >
                                    <CircularProgress size={40} />
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{
                                            fontWeight: 500,
                                        }}
                                    >
                                        Loading messages...
                                    </Typography>
                                </Box>
                            ) : messagesWithDateHeaders.length === 0 ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        height: "100%",
                                        color: "text.secondary",
                                    }}
                                >
                                    <Chat sx={{ fontSize: 64, mb: 2, opacity: 0.25, color: WB_UI.color.whatsappDark }} />
                                    <Typography variant="body1">
                                        No messages yet. Start a conversation!
                                    </Typography>
                                </Box>
                            ) : (
                                messagesWithDateHeaders.map((message) => {
                        // Date header row
                        if (message.__type === "date-header") {
                            return (
                                <Box
                                    key={message.id}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        my: 1.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 1.5,
                                            backgroundColor: "rgba(0, 0, 0, 0.15)",
                                            color: "white",
                                            fontSize: "0.75rem",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {message.label}
                                    </Box>
                                </Box>
                            );
                        }

                        // Check if this is a template message
                        if (message.is_template && message.template_id) {
                            const templateDetails = templateDetailsCache[message.template_id];
                            
                            if (templateDetails) {
                                return (
                                    <Box
                                        key={message.id}
                                        sx={{
                                            display: "flex",
                                            justifyContent:
                                                message.sender === "user" ? "flex-start" : "flex-end",
                                            mb: 1,
                                        }}
                                    >
                                        <TemplateMessageBubble
                                            templateDetails={templateDetails}
                                            payload={message.payload}
                                            mediaLink={message.media_link}
                                            mediaType={message.media_type}
                                            sender={message.sender}
                                            timestamp={message.timestamp}
                                            status={message.status}
                                            isBotReply={message.is_bot_reply}
                                            senderName={message.sender_name}
                                        />
                                    </Box>
                                );
                            } else if (loadingTemplates[message.template_id]) {
                                return (
                                    <Box
                                        key={message.id}
                                        sx={{
                                            display: "flex",
                                            justifyContent:
                                                message.sender === "user" ? "flex-start" : "flex-end",
                                            mb: 1,
                                        }}
                                    >
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 1.5,
                                                maxWidth: "70%",
                                                backgroundColor:
                                                    message.sender === "user"
                                                        ? "background.paper"
                                                        : message.is_bot_reply
                                                            ? "#6B46C1"
                                                            : "#dcf8c6",
                                                color:
                                                    message.sender === "user"
                                                        ? "text.primary"
                                                        : message.is_bot_reply
                                                            ? "rgba(255,255,255,0.95)"
                                                            : "text.primary",
                                                borderRadius: 2,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                            }}
                                        >
                                            <CircularProgress size={16} sx={{ color: "inherit" }} />
                                            <Typography variant="body2">
                                                Loading template...
                                            </Typography>
                                        </Paper>
                                    </Box>
                                );
                            } else {
                                return (
                                    <Box
                                        key={message.id}
                                        sx={{
                                            display: "flex",
                                            justifyContent:
                                                message.sender === "user" ? "flex-start" : "flex-end",
                                            mb: 1,
                                        }}
                                    >
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 1.5,
                                                maxWidth: "70%",
                                                backgroundColor:
                                                    message.sender === "user"
                                                        ? "background.paper"
                                                        : message.is_bot_reply
                                                            ? "#6B46C1"
                                                            : "#dcf8c6",
                                                color:
                                                    message.sender === "user"
                                                        ? "text.primary"
                                                        : message.is_bot_reply
                                                            ? "rgba(255,255,255,0.95)"
                                                            : "text.primary",
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Typography variant="body1">
                                                {message.text || "Template message (unavailable)"}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: message.sender === "user" ? "flex-start" : "flex-end",
                                                    mt: 0.5,
                                                    gap: 0.25,
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            opacity: 0.7,
                                                        }}
                                                    >
                                                        {message.timestamp}
                                                    </Typography>
                                                    {message.sender === "bot" && (
                                                        <MessageStatusIcon status={message.status} />
                                                    )}
                                                </Box>
                                                {message.sender === "bot" && message.sender_name && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            opacity: 0.85,
                                                            fontSize: "0.7rem",
                                                        }}
                                                    >
                                                        {message.sender_name}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Paper>
                                    </Box>
                                );
                            }
                        }

                        // Regular text message or media message
                        const hasMedia = message.media_link && message.media_type && message.media_type !== "TEXT";
                        const mediaUrl = message.media_link 
                            ? (message.media_link.startsWith("http://") || message.media_link.startsWith("https://"))
                                ? message.media_link
                                : `https://pub-b8f74165803246fbaee6ed60fe08c081.r2.dev/${message.media_link.startsWith("/") ? message.media_link.slice(1) : message.media_link}`
                            : null;
                        
                        return (
                            <Box
                                key={message.id}
                                sx={{
                                    display: "flex",
                                    justifyContent:
                                        message.sender === "user" ? "flex-start" : "flex-end",
                                    mb: 1,
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: hasMedia ? 0 : 1.5,
                                        maxWidth: "70%",
                                        backgroundColor:
                                            message.sender === "user"
                                                ? "background.paper"
                                                : message.is_bot_reply
                                                    ? "#6B46C1"
                                                    : "#dcf8c6",
                                        color:
                                            message.sender === "user"
                                                ? "text.primary"
                                                : message.is_bot_reply
                                                    ? "rgba(255,255,255,0.95)"
                                                    : "black",
                                        borderRadius: 2,
                                        overflow: "hidden",
                                    }}
                                >
                                    {/* Media Display + Download */}
                                    {hasMedia && mediaUrl && (
                                        <Box sx={{ mb: message.text ? 1 : 0 }}>
                                            {/* Viewable media: IMAGE & VIDEO */}
                                            {message.media_type === "IMAGE" && (
                                                <>
                                                    <Box
                                                        component="img"
                                                        src={mediaUrl}
                                                        alt="Image"
                                                        onError={(e) => {
                                                            console.error("Image load error:", e);
                                                            e.target.style.display = "none";
                                                        }}
                                                        sx={{
                                                            width: "100%",
                                                            maxHeight: 300,
                                                            display: "block",
                                                            objectFit: "contain",
                                                            backgroundColor: "#f5f5f5",
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent: "flex-end",
                                                            mt: 0.5,
                                                            px: 1.5,
                                                        }}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            component="a"
                                                            href={mediaUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            download
                                                            sx={{
                                                                color:
                                                                    message.sender === "user"
                                                                        ? WB_UI.color.whatsappDark
                                                                        : message.is_bot_reply
                                                                            ? "rgba(255,255,255,0.9)"
                                                                            : WB_UI.color.whatsappDark,
                                                            }}
                                                        >
                                                            <Download fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </>
                                            )}
                                            {message.media_type === "VIDEO" && (
                                                <>
                                                    <Box
                                                        component="video"
                                                        src={mediaUrl}
                                                        controls
                                                        sx={{
                                                            width: "100%",
                                                            maxHeight: 300,
                                                            display: "block",
                                                            backgroundColor: "#000",
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent: "flex-end",
                                                            mt: 0.5,
                                                            px: 1.5,
                                                        }}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            component="a"
                                                            href={mediaUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            download
                                                            sx={{
                                                                color:
                                                                    message.sender === "user"
                                                                        ? WB_UI.color.whatsappDark
                                                                        : message.is_bot_reply
                                                                            ? "rgba(255,255,255,0.9)"
                                                                            : WB_UI.color.whatsappDark,
                                                            }}
                                                        >
                                                            <Download fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </>
                                            )}

                                            {/* Non-viewable media: DOCUMENT & AUDIO -> download only */}
                                            {message.media_type === "DOCUMENT" && (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        p: 1.5,
                                                        backgroundColor: "#f5f5f5",
                                                        justifyContent: "space-between",
                                                    }}
                                                >
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Description sx={{ fontSize: 32, color: WB_UI.color.whatsappDark }} />
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                Document
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {message.media_link.split("/").pop() || "Document"}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <IconButton
                                                        size="small"
                                                        component="a"
                                                        href={mediaUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        download
                                                    >
                                                        <Download fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            )}
                                            {message.media_type === "AUDIO" && (
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        backgroundColor: "#f5f5f5",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        Audio file
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        component="a"
                                                        href={mediaUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        download
                                                    >
                                                        <Download fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                    
                                    {/* Text Content */}
                                    {message.text && (
                                        <Box sx={{ px: hasMedia ? 1.5 : 0, pb: hasMedia ? 1 : 0, pt: hasMedia ? 1 : 0 }}>
                                            <Typography variant="body1">{message.text}</Typography>
                                        </Box>
                                    )}
                                    
                                    {/* Timestamp and Status */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: message.sender === "user" ? "flex-start" : "flex-end",
                                            px: 1.5,
                                            pb: 1,
                                            gap: 0.25,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    opacity: 0.7,
                                                }}
                                            >
                                                {message.timestamp}
                                            </Typography>
                                            {message.sender === "bot" && (
                                                <MessageStatusIcon status={message.status} />
                                            )}
                                        </Box>
                                        {message.sender === "bot" && message.sender_name && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                {message.is_bot_reply ? (
                                                    <SmartToy sx={{ fontSize: 14, opacity: 0.85 }} />
                                                ) : (
                                                    <Person sx={{ fontSize: 14, opacity: 0.85 }} />
                                                )}
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        opacity: 0.85,
                                                        fontSize: "0.7rem",
                                                    }}
                                                >
                                                    Replied by {message.sender_name}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Paper>
                            </Box>
                                );
                            })
                        )}
                        </Box>

                        {/* Scroll to Bottom Button */}
                        {showScrollButton && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: 80,
                                    right: 16,
                                    zIndex: 10,
                                }}
                            >
                                <IconButton
                                    onClick={scrollToBottom}
                                    sx={{
                                        backgroundColor: WB_UI.color.whatsappDark,
                                        color: "white",
                                        boxShadow: 3,
                                        "&:hover": {
                                            backgroundColor: "#064e47",
                                        },
                                        width: 48,
                                        height: 48,
                                    }}
                                >
                                    <Badge
                                        badgeContent={unreadCount}
                                        color="error"
                                        invisible={unreadCount === 0}
                                        anchorOrigin={{
                                            vertical: "top",
                                            horizontal: "right",
                                        }}
                                    >
                                        <KeyboardArrowDown />
                                    </Badge>
                                </IconButton>
                            </Box>
                        )}

                        {/* Message Input Area */}
                        <Box
                            sx={{
                                p: 2,
                                borderTop: 1,
                                borderColor: "divider",
                                backgroundColor: "background.paper",
                                position: "relative",
                            }}
                        >
                {/* Media Preview */}
                {mediaPreviewUrl && mediaFile && (
                    <Box
                        sx={{
                            mb: 2,
                            p: 1,
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            backgroundColor: "background.default",
                            position: "relative",
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={handleCancelMedia}
                            sx={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                backgroundColor: "error.main",
                                color: "white",
                                zIndex: 1,
                                "&:hover": {
                                    backgroundColor: "error.dark",
                                },
                            }}
                        >
                            <Cancel fontSize="small" />
                        </IconButton>
                        {mediaType === "image" && (
                            <Box
                                component="img"
                                src={mediaPreviewUrl}
                                alt="Preview"
                                sx={{
                                    maxWidth: "100%",
                                    maxHeight: "200px",
                                    borderRadius: 1,
                                    display: "block",
                                }}
                            />
                        )}
                        {mediaType === "video" && (
                            <Box
                                component="video"
                                src={mediaPreviewUrl}
                                controls
                                sx={{
                                    maxWidth: "100%",
                                    maxHeight: "200px",
                                    borderRadius: 1,
                                    display: "block",
                                }}
                            />
                        )}
                        {(mediaType === "document" || mediaType === "audio") && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1 }}>
                                {mediaType === "document" ? (
                                    <InsertDriveFile sx={{ fontSize: 40, color: WB_UI.color.whatsappDark }} />
                                ) : (
                                    <Description sx={{ fontSize: 40, color: WB_UI.color.whatsappDark }} />
                                )}
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                    {mediaFile.name}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Hidden file input */}
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleMediaFileChange}
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                    style={{ display: "none" }}
                />

                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <IconButton
                        onClick={() => fileInputRef.current?.click()}
                        disabled={!selectedContact || uploadingMedia || sendingMedia}
                        sx={{
                            color: WB_UI.color.whatsappDark,
                        }}
                    >
                        <AttachFile />
                    </IconButton>
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        placeholder={mediaFile ? "Add a caption (optional)..." : "Type a message..."}
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        size="small"
                        disabled={uploadingMedia || sendingMedia}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        ref={emojiButtonRef}
                                        onClick={handleEmojiButtonClick}
                                        disabled={uploadingMedia || sendingMedia}
                                    >
                                        <EmojiEmotions />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <IconButton
                        onClick={mediaFile ? handleMediaUpload : handleSendMessage}
                        disabled={
                            (mediaFile ? uploadingMedia || sendingMedia : !messageInput.trim() || loading) ||
                            !selectedContact
                        }
                        sx={{
                            backgroundColor: WB_UI.color.whatsappDark,
                            color: "white",
                            "&:hover": {
                                backgroundColor: "#064e47",
                            },
                            "&.Mui-disabled": {
                                backgroundColor: "grey.300",
                                color: "grey.500",
                            },
                        }}
                    >
                        {(loading || uploadingMedia || sendingMedia) ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            <Send />
                        )}
                    </IconButton>
                </Box>
                
                {/* Emoji Picker */}
                <Popper
                    open={showEmojiPicker}
                    anchorEl={emojiButtonRef.current}
                    placement="top-end"
                    sx={{
                        zIndex: 1300,
                        mb: 1,
                    }}
                >
                    <ClickAwayListener onClickAway={() => setShowEmojiPicker(false)}>
                        <Paper
                            elevation={8}
                            sx={{
                                p: 2,
                                maxWidth: "280px",
                                maxHeight: "300px",
                                overflowY: "auto",
                                "&::-webkit-scrollbar": {
                                    width: "8px",
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    backgroundColor: "rgba(0,0,0,0.2)",
                                    borderRadius: "4px",
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(8, 1fr)",
                                    gap: 0.5,
                                }}
                            >
                                {professionalEmojis.map((emoji, index) => (
                                    <IconButton
                                        key={index}
                                        onClick={() => handleEmojiClick(emoji)}
                                        sx={{
                                            fontSize: "24px",
                                            width: "36px",
                                            height: "36px",
                                            padding: 0,
                                            "&:hover": {
                                                backgroundColor: "action.hover",
                                            },
                                        }}
                                    >
                                        {emoji}
                                    </IconButton>
                                ))}
                            </Box>
                        </Paper>
                        </ClickAwayListener>
                    </Popper>
                </Box>
                    </>
                ) : (
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            color: "text.secondary",
                        }}
                    >
                        <Chat sx={{ fontSize: 96, mb: 2, opacity: 0.25, color: WB_UI.color.whatsappDark }} />
                        <Typography variant="h6">
                            Select a contact to start chatting
                        </Typography>
                    </Box>
                )}
            </Paper>
        </Box>

        {/* CRM dialogs: CRM-ADMIN / CRM-USER only */}
        {hasCrmRole && (
        <>
        <Dialog open={addCrmOpen} onClose={() => { if (!savingCrm) { setAddCrmOpen(false); setCrmForm(EMPTY_CRM_FORM); } }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: "0.95rem", pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: "50%", backgroundColor: WB_UI.color.softInfoBg, border: `1px solid ${WB_UI.color.border}`, display: "grid", placeItems: "center" }}>
                        <PersonAddOutlined sx={{ fontSize: 16, color: WB_UI.color.whatsappDark }} />
                    </Box>
                    {crmExists ? "Update CRM Contact" : "Add to CRM Contacts"}
                </Box>
                <IconButton size="small" onClick={() => { if (!savingCrm) { setAddCrmOpen(false); setCrmForm(EMPTY_CRM_FORM); } }} disabled={savingCrm}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2.5 }}>
                <Box sx={{ mb: 2, px: 1.5, py: 1, borderRadius: 1.5, backgroundColor: "#f9fafb", border: "1px solid #e5e7eb" }}>
                    <Typography variant="caption" color="text.secondary">Phone number</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{selectedContact}</Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <TextField fullWidth label="Name" placeholder="e.g. Rahul Sharma" value={crmForm.name}
                        onChange={(e) => setCrmForm((f) => ({ ...f, name: e.target.value }))} size="small" autoFocus
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    <TextField fullWidth label="Email" placeholder="e.g. rahul@example.com" value={crmForm.email}
                        onChange={(e) => setCrmForm((f) => ({ ...f, email: e.target.value }))} size="small" type="email"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    <TextField fullWidth label="Company Name" placeholder="e.g. Acme Corp" value={crmForm.company_name}
                        onChange={(e) => setCrmForm((f) => ({ ...f, company_name: e.target.value }))} size="small"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    <FormControl fullWidth size="small">
                        <InputLabel>Source</InputLabel>
                        <Select label="Source" value={crmForm.source}
                            onChange={(e) => setCrmForm((f) => ({ ...f, source: e.target.value }))}
                            sx={{ borderRadius: 2 }}>
                            <MenuItem value=""><em>Select source</em></MenuItem>
                            {SOURCE_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                    </FormControl>
                    {/* Status toggle */}
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: "block", fontWeight: 500 }}>Status</Typography>
                        <Box sx={{ display: "flex", gap: 0.75 }}>
                            {[{ value: "interested", label: "Interested" }, { value: "not_interested", label: "Not Interested" }].map(({ value: v, label }) => {
                                const active = crmForm.status === v;
                                return (
                                    <Button key={v} size="small" onClick={() => setCrmForm((f) => ({ ...f, status: v }))}
                                        sx={{
                                            textTransform: "none", fontSize: "0.75rem", fontWeight: 600, borderRadius: 2, px: 1.5,
                                            border: `1px solid ${active ? (v === "interested" ? "#bbf7d0" : "#fecaca") : "#e5e7eb"}`,
                                            backgroundColor: active ? (v === "interested" ? "#f0fdf4" : "#fef2f2") : "transparent",
                                            color: active ? (v === "interested" ? "#166534" : "#991b1b") : "text.secondary",
                                            "&:hover": { backgroundColor: v === "interested" ? "#dcfce7" : "#fee2e2" },
                                        }}
                                    >{label}</Button>
                                );
                            })}
                        </Box>
                    </Box>
                    {/* Bot toggle — WB-AGENT only */}
                    {isWbAgent && (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1,
                        borderRadius: 2, border: `1px solid ${crmForm.is_bot_enabled ? "#ddd6fe" : "#e5e7eb"}`,
                        backgroundColor: crmForm.is_bot_enabled ? "#f5f3ff" : "#f9fafb" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <SmartToy sx={{ fontSize: 18, color: crmForm.is_bot_enabled ? "#7c3aed" : "#9ca3af" }} />
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.8rem", color: crmForm.is_bot_enabled ? "#7c3aed" : "text.primary" }}>
                                    Auto Bot Reply
                                </Typography>
                                <Typography variant="caption" color="text.secondary">Automatically reply using bot</Typography>
                            </Box>
                        </Box>
                        <Switch size="small" checked={crmForm.is_bot_enabled}
                            onChange={(e) => setCrmForm((f) => ({ ...f, is_bot_enabled: e.target.checked }))}
                            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#7c3aed" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#7c3aed" } }} />
                    </Box>
                    )}
                </Box>
                {crmExists && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                        This contact already exists in CRM. Only the fields you fill in will be updated.
                    </Typography>
                )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button size="small" onClick={() => { setAddCrmOpen(false); setCrmForm(EMPTY_CRM_FORM); }} disabled={savingCrm}
                    sx={{ textTransform: "none", borderRadius: 2, border: "1px solid #e5e7eb", color: "text.secondary" }}>
                    Cancel
                </Button>
                <Button onClick={handleAddToCrm} disabled={savingCrm} variant="contained" size="small"
                    sx={{ backgroundColor: "#1a2333", "&:hover": { backgroundColor: "#374151" }, textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
                    {savingCrm ? "Saving…" : crmExists ? "Update Contact" : "Add to CRM"}
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog open={editNameOpen} onClose={() => !savingCrm && setEditNameOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: "0.95rem", pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EditOutlined sx={{ fontSize: 18, color: "#1a2333" }} />
                    Edit Contact Name
                </Box>
                <IconButton size="small" onClick={() => !savingCrm && setEditNameOpen(false)} disabled={savingCrm}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <TextField
                    label="Phone"
                    value={selectedContact || ""}
                    fullWidth
                    disabled
                    size="small"
                    sx={{ mb: 2, mt: 1 }}
                />
                <TextField
                    label="Contact Name"
                    value={crmNameInput}
                    onChange={(e) => setCrmNameInput(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="Enter contact name"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === "Enter" && crmNameInput.trim()) handleEditName(); }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={() => !savingCrm && setEditNameOpen(false)} disabled={savingCrm} color="inherit" size="small">Cancel</Button>
                <Button
                    onClick={handleEditName}
                    disabled={savingCrm || !crmNameInput.trim()}
                    variant="contained"
                    size="small"
                    sx={{ backgroundColor: "#1a2333", "&:hover": { backgroundColor: "#374151" }, textTransform: "none" }}
                >
                    {savingCrm ? <CircularProgress size={16} color="inherit" /> : "Save Name"}
                </Button>
            </DialogActions>
        </Dialog>
        </>
        )}
        </>
    );
};

export default FullScreenChatWindow;

